import {
  ChevronRight,
  ExternalLink,
  MousePointerClick,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/shadcn/button';
import { ErrorPageImage } from '@/components/errorPage/errorPage.image';
import { useState } from 'react';
import { cn } from '@/shadcn/lib/utils';
import { Copy } from '@/shadcn/copy';
import { toast } from 'sonner';
import { TestId } from '@/types/testId';

type Props = {
  onHomeClick: () => void;
  onReloadPageClick?: () => void;
  buttonLabel?: string;
  description?: React.ReactNode;
  errorMessage?: string;
};

export const ErrorPage = ({
  onHomeClick,
  onReloadPageClick,
  buttonLabel = 'Take me to the home page',
  description = 'Sorry, we encountered an error.',
  errorMessage
}: Props) => {
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);

  const onCopyErrorClicked = () => {
    toast.success('Error copied to clipboard');
  };

  //? Note: We can't use the Navbar here since we're out of the router context
  return (
    <section className="bg-card dark:bg-background scrollbar-stable flex size-full h-screen flex-col items-center overflow-auto md:mt-0 md:justify-start md:pt-[7%]">
      <div className="flex w-full max-w-5xl flex-col items-center justify-center p-4">
        <ErrorPageImage className="mb-10 w-full max-w-[150px] select-none md:max-w-[200px]" />
        {description && <div className="text-center">{description}</div>}
        <div className="mt-4 flex flex-col items-center justify-center gap-1 select-none md:flex-row md:gap-4">
          {onReloadPageClick && (
            <>
              <Button
                variant="outline"
                onClick={onReloadPageClick}
                aria-label="Reload page"
                className="group/reload"
                data-testid={TestId.ErrorPage.ReloadButton}
              >
                <RefreshCw className="transition-transform duration-500 ease-in-out group-hover/reload:rotate-[360deg]" />
                Try to reload the page
              </Button>
              <span className="text-muted-foreground text-md italic">or</span>
            </>
          )}
          <Button
            onClick={onHomeClick}
            aria-label={buttonLabel ?? 'Take me home'}
            data-testid={TestId.ErrorPage.RedirectButton}
          >
            {buttonLabel}
            <ExternalLink />
          </Button>
        </div>

        {errorMessage && (
          <div
            className={cn(
              'group/error border-border bg-muted dark:bg-card text-muted-foreground dark:text-card-foreground relative mt-14 box-border flex h-24 w-full max-w-3xl justify-center gap-4 overflow-hidden rounded-md border p-4 text-left text-xs leading-4.5 text-wrap transition-all duration-300 ease-in-out',
              isErrorExpanded && 'h-auto',
              !isErrorExpanded &&
                'cursor-pointer pb-0 select-none hover:brightness-95'
            )}
            onClick={() => setIsErrorExpanded(true)}
            data-testid={TestId.ErrorPage.ErrorMessage}
          >
            <p className="w-full overflow-hidden text-left font-mono text-wrap whitespace-break-spaces italic">
              <span className="mr-1 inline-block align-sub select-none">
                <ChevronRight size={14} />
              </span>
              <span>{errorMessage}</span>
            </p>
            {!isErrorExpanded ? (
              <>
                <div className="from-background/90 dark:from-card/90 pointer-events-none absolute right-0 bottom-0 left-0 h-full bg-gradient-to-t to-transparent"></div>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-base opacity-0 backdrop-blur-none transition-all duration-300 ease-in-out group-hover/error:opacity-100 group-hover/error:backdrop-blur-[1px]">
                  <span>Click to open the error details</span>
                  <MousePointerClick />
                </div>
              </>
            ) : (
              <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-150 ease-in-out group-hover/error:opacity-100">
                <Copy
                  textToCopy={errorMessage}
                  onClick={onCopyErrorClicked}
                  tooltipText="Click to copy the error message"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
